import { useEffect, useState } from 'react';
import axios from 'axios';
import { codedIdRole } from './accountIds';

const STORAGE_KEY = 'hospitalBranding';

/** Hospital public id inside an old-format id, or the coded id itself (the server resolves its code). */
export function hospitalIdFromAnyId(value) {
  const id = String(value || '').trim().toUpperCase();
  const embedded = id.match(/HSP-[A-Z]{3}-[A-F0-9]{6}/);
  if (embedded) return embedded[0];
  const admin = id.match(/^ADM-([A-Z]{3}-[A-F0-9]{6})(-\d{2,})?$/);
  if (admin) return `HSP-${admin[1]}`;
  return codedIdRole(id) ? id : '';
}

export function readStoredBranding() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return saved && saved.publicId ? saved : null;
  } catch (error) {
    return null;
  }
}

/** Saves the signed-in user's hospital so its logo can be shown on the next visit to a login page. */
export function rememberHospitalBranding(user) {
  if (!user?.hospitalId) return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    publicId: user.hospitalId,
    name: user.hospitalName || '',
    logoUrl: user.hospitalLogoUrl || ''
  }));
}

async function fetchBranding(publicId) {
  try {
    const response = await axios.get(`/api/hospitals/${encodeURIComponent(publicId)}/branding`);
    return response.data?.branding || null;
  } catch (error) {
    return null;
  }
}

/**
 * Branding for a login page. Uses the id being typed when it names a hospital,
 * otherwise `?hospital=` from the URL, otherwise the last hospital signed in on this device.
 */
export function useHospitalBranding(identifier) {
  const [branding, setBranding] = useState(readStoredBranding);
  const typedHospital = hospitalIdFromAnyId(identifier);
  const queryHospital = typeof window !== 'undefined'
    ? hospitalIdFromAnyId(new URLSearchParams(window.location.search).get('hospital'))
    : '';
  const target = typedHospital || queryHospital || readStoredBranding()?.publicId || '';

  useEffect(() => {
    if (!target) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const result = await fetchBranding(target);
      if (!cancelled) setBranding(result);
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [target]);

  return target ? branding : null;
}

export default useHospitalBranding;
